import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import * as LuIcons from 'react-icons/lu';
import SafeIcon from '../../common/SafeIcon';
import { format } from 'date-fns';

const { LuUsers, LuSearch, LuTrash2, LuCopy, LuCheck, LuCheckCircle, LuXCircle, LuClock } = LuIcons;

const AdminAffiliates = () => {
  const [marketers, setMarketers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [copiedId, setCopiedId] = useState(null);

  useEffect(() => { fetchMarketers(); }, []);

  const fetchMarketers = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('marketers')
      .select('*')
      .order('created_at', { ascending: false });
    if (!error && data) setMarketers(data);
    setLoading(false);
  };

  const handleDelete = async (id) => {
    if (window.confirm('هل أنت متأكد من حذف هذا المسوق؟')) {
      await supabase.from('marketers').delete().eq('id', id);
      fetchMarketers();
    }
  };

  const updateStatus = async (id, newStatus) => {
    await supabase.from('marketers').update({ status: newStatus }).eq('id', id);
    fetchMarketers();
  };

  const copyCode = (id, code) => {
    navigator.clipboard.writeText(code);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const filteredMarketers = marketers.filter(m =>
    m.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    m.phone.includes(searchTerm) ||
    m.code.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved': return <span className="flex items-center gap-1 bg-emerald-100 text-emerald-700 px-2 py-1 rounded-lg text-xs font-bold"><SafeIcon icon={LuCheckCircle} className="w-3 h-3" />مقبول</span>;
      case 'rejected': return <span className="flex items-center gap-1 bg-red-100 text-red-700 px-2 py-1 rounded-lg text-xs font-bold"><SafeIcon icon={LuXCircle} className="w-3 h-3" />مرفوض</span>;
      default: return <span className="flex items-center gap-1 bg-amber-100 text-amber-700 px-2 py-1 rounded-lg text-xs font-bold"><SafeIcon icon={LuClock} className="w-3 h-3" />قيد المراجعة</span>;
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden" dir="rtl">
      <div className="p-4 sm:p-6 border-b border-slate-100 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
            <SafeIcon icon={LuUsers} className="w-6 h-6 text-indigo-600" />
            شبكة المسوقين
          </h2>
          <p className="text-sm text-slate-500 mt-1">إدارة حسابات المسوقين بالعمولة والموافقة عليهم</p>
        </div>
        <div className="relative w-full sm:w-64">
          <SafeIcon icon={LuSearch} className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            placeholder="بحث بالاسم، الهاتف أو الكود..."
            className="w-full pe-10 ps-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none text-sm"
          />
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-slate-100">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-6 py-4 text-start text-xs font-black text-slate-500 uppercase">المسوق</th>
              <th className="px-6 py-4 text-start text-xs font-black text-slate-500 uppercase">الحالة</th>
              <th className="px-6 py-4 text-start text-xs font-black text-slate-500 uppercase">الكود الخاص به</th>
              <th className="px-6 py-4 text-start text-xs font-black text-slate-500 uppercase">تاريخ الانضمام</th>
              <th className="px-6 py-4 text-end text-xs font-black text-slate-500 uppercase">إجراءات</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {loading ? (
              <tr><td colSpan="5" className="text-center py-10 text-slate-400">جاري التحميل...</td></tr>
            ) : filteredMarketers.length === 0 ? (
              <tr><td colSpan="5" className="text-center py-10 text-slate-400">لا يوجد مسوقين.</td></tr>
            ) : (
              filteredMarketers.map(marketer => (
                <tr key={marketer.id} className="hover:bg-slate-50 transition-colors">
                  <td className="px-6 py-4">
                    <div className="font-bold text-slate-900">{marketer.name}</div>
                    <div className="text-sm text-slate-500 mt-0.5" dir="ltr">{marketer.phone}</div>
                  </td>
                  <td className="px-6 py-4">
                    {getStatusBadge(marketer.status)}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <span className="bg-indigo-50 text-indigo-700 font-black px-3 py-1 rounded-lg border border-indigo-100 text-sm tracking-widest">
                        {marketer.code}
                      </span>
                      <button onClick={() => copyCode(marketer.id, marketer.code)} className="text-slate-400 hover:text-indigo-600">
                        <SafeIcon icon={copiedId === marketer.id ? LuCheck : LuCopy} className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-500">
                    {format(new Date(marketer.created_at), 'yyyy-MM-dd')}
                  </td>
                  <td className="px-6 py-4 text-end">
                    <div className="flex items-center justify-end gap-2">
                      {marketer.status !== 'approved' && (
                        <button onClick={() => updateStatus(marketer.id, 'approved')} className="text-emerald-600 hover:text-emerald-800 bg-emerald-50 hover:bg-emerald-100 px-3 py-1.5 rounded-lg text-xs font-bold transition-colors">
                          قبول
                        </button>
                      )}
                      {marketer.status !== 'rejected' && (
                        <button onClick={() => updateStatus(marketer.id, 'rejected')} className="text-red-600 hover:text-red-800 bg-red-50 hover:bg-red-100 px-3 py-1.5 rounded-lg text-xs font-bold transition-colors">
                          رفض
                        </button>
                      )}
                      <button onClick={() => handleDelete(marketer.id)} className="text-slate-400 hover:text-red-600 p-2 rounded-lg transition-colors ms-2" title="حذف">
                        <SafeIcon icon={LuTrash2} className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminAffiliates;